import { localNum } from "@faire/web/common/localization";
import { Button } from "@faire/web/slate/Button";
import { Typography } from "@faire/web/slate/Typography";
import {
  StrictLocalMsg,
  chunkTransformations,
} from "@faire/web/ui/lib/localization";
import { observer } from "mobx-react";
import React, { useState } from "react";

import { useFirstOrderIncentiveTimeRemaining } from "@faire/retailer/hooks/firstOrderIncentive/useFirstOrderIncentiveTimeRemaining";
import { useInsiderMembershipInformation } from "@faire/retailer/hooks/insider/useInsiderMembershipInformation";

import { LazyAutoEnrollModal } from "./LazyAutoEnrollModal";

const InsiderAutoEnrollBannerBase: React.FC = () => {
  const [isModalShowing, setIsModalShowing] = useState(false);
  const { insiderMembershipInformation } = useInsiderMembershipInformation();
  const { daysRemaining, hoursRemaining } = useFirstOrderIncentiveTimeRemaining(
    insiderMembershipInformation?.current_period_end_at ?? 0
  );
  const daysLeftNumber = localNum(daysRemaining);
  const onViewDetailClick = () => {
    setIsModalShowing(true);
  };
  const hideModal = () => {
    setIsModalShowing(false);
  };

  const getBannerText = () => {
    if (hoursRemaining < 24) {
      return (
        <StrictLocalMsg
          defaultMessage="<MediumFormatting>Last day to save!</MediumFormatting> Free shipping with thousands of brands available with your welcome offer."
          description={{
            text: "A banner at the top of the page telling the user that it's the last day to use their sign-up offer.",
            img: "https://cdn.faire.com/fastly/ea48b9820454d14c6771eeca05ed53e0c2d0328c3b76aa171d11dfd563722665.png",
          }}
          values={{
            MediumFormatting: chunkTransformations.Medium,
          }}
        />
      );
    } else if (daysRemaining <= 7) {
      return (
        <StrictLocalMsg
          defaultMessage="<MediumFormatting>Only {daysLeftNumber} days left!</MediumFormatting> Free shipping with thousands of brands available with your welcome offer."
          description={{
            text: "A banner at the top of the page telling the user the number of days left to use their sign-up offer.",
            img: "https://cdn.faire.com/fastly/020f1ed30e6bcd95d0d941e63a640fa8137394a022926ae5f23e9a09949d20a5.png",
          }}
          values={{
            MediumFormatting: chunkTransformations.Medium,
            daysLeftNumber,
          }}
        />
      );
    } else {
      return (
        <StrictLocalMsg
          defaultMessage="<MediumFormatting>Free shipping with thousands of brands!</MediumFormatting> Available with your welcome offer."
          description={{
            text: "A banner at the top of the page telling the user about the sign-up offer.",
            img: "https://cdn.faire.com/fastly/f692de7c527ac1492065409c1b025fc19920e0f78cc492cd37084aea554ef44d.png",
          }}
          values={{
            MediumFormatting: chunkTransformations.Medium,
          }}
        />
      );
    }
  };

  return (
    <>
      <Typography align="center" data-test-id="insider-auto-enroll-banner">
        {getBannerText()}{" "}
        <Button variant="tertiary" onClick={onViewDetailClick}>
          <StrictLocalMsg
            defaultMessage="View details"
            description={{
              text: "View detail CTA text to bring up modal",
              img: "https://cdn.faire.com/fastly/8531138aceb26f7d48c01f9f46d45943b0181c019381964c4b90fcc49fb57822.png",
            }}
          />
        </Button>
      </Typography>
      {isModalShowing ? <LazyAutoEnrollModal onCloseModal={hideModal} /> : null}
    </>
  );
};

export const InsiderAutoEnrollBanner = observer(InsiderAutoEnrollBannerBase);
